import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

/**
 * Centered spinner used as the Suspense fallback while a lazily loaded route
 * chunk is fetched, and (via DelayedPageLoader) as the route guards' auth-check
 * fallback.
 *
 * Height is configurable via `className` because the same component is used in
 * two contexts: the top-level App boundary (full viewport, default `h-screen`)
 * and the nested AuthenticatedLayout boundary, where it must fill only the
 * content area (`h-full`) so the sidebar/footer aren't pushed around.
 */
export function PageLoader({
  className = 'h-screen',
  delayMs = 0,
}: {
  className?: string;
  delayMs?: number;
}) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShown(true), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs]);

  const fadeIn = shown && delayMs > 0 ? ' animate-in fade-in duration-300' : '';

  return (
    // The region mounts empty and fills after: a status region announces only
    // what changes once it exists, and never an aria-label.
    <div
      role="status"
      className={`flex items-center justify-center ${className}${fadeIn}`}
    >
      {shown ? (
        <>
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="sr-only">Loading</span>
        </>
      ) : null}
    </div>
  );
}

export const AUTH_CHECK_SPINNER_DELAY_MS = 350;

/**
 * The route guards' fallback during the initial auth check: blank at first,
 * then the spinner fades in. That check usually resolves well under a second,
 * and a loader that flashes briefly is more jarring than a blank frame — so
 * only genuinely slow checks ever show the spinner.
 */
export function DelayedPageLoader() {
  return <PageLoader delayMs={AUTH_CHECK_SPINNER_DELAY_MS} />;
}
